<template>
  <div class="main-layout">

    <!-- KPI row -->
    <v-row class="kpi-row">
      <v-col cols="12" sm="3">
        <kpi-card
          label="Total Devices"
          v-bind:value="items.length"
          color="primary"
          icon="mdi-monitor-multiple"
          v-bind:sub="`across ${groups.length} group${groups.length !== 1 ? 's' : ''}`"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <kpi-card
          label="Online"
          v-bind:value="onlineCount"
          color="success"
          icon="mdi-check-circle-outline"
          sub="last seen < 15 min"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <kpi-card
          label="Offline"
          v-bind:value="offlineCount"
          color="error"
          icon="mdi-alert-circle-outline"
          sub="last seen > 15 min"
        />
      </v-col>
      <v-col cols="12" sm="3">
        <kpi-card
          label="Bandwidth Today"
          v-bind:value="summary ? format_bytes((summary.rx_today || 0) + (summary.tx_today || 0)) : '—'"
          color="info"
          icon="mdi-swap-vertical"
          v-bind:sub="summary ? `↓ ${format_bytes(summary.rx_today || 0)}  ↑ ${format_bytes(summary.tx_today || 0)}` : 'loading…'"
        />
      </v-col>
    </v-row>

    <!-- Workspace -->
    <div class="workspace-row">

      <!-- Device table -->
      <div class="table-col">
        <v-card flat outlined class="table-card">

          <div class="table-toolbar d-flex flex-wrap align-center gap-2 pa-3">
            <v-text-field
              v-model="tableSearch"
              placeholder="Search by name, MAC, customer…"
              prepend-inner-icon="mdi-magnify"
              outlined dense hide-details clearable
              style="max-width:300px"
            />
            <v-select
              v-model="filterGroupId"
              v-bind:items="groupFilterItems"
              item-text="label"
              item-value="value"
              label="Group"
              outlined dense hide-details clearable
              style="max-width:180px"
            />
            <v-select
              v-model="filterStatus"
              v-bind:items="statusFilterItems"
              item-text="label"
              item-value="value"
              label="Status"
              outlined dense hide-details clearable
              style="max-width:140px"
            />
            <v-select
              v-model="filterEnabled"
              v-bind:items="enabledFilterItems"
              item-text="label"
              item-value="value"
              label="Enabled"
              outlined dense hide-details clearable
              style="max-width:140px"
            />
            <v-spacer />
            <v-btn color="primary" depressed v-on:click="createItem">
              <v-icon left>mdi-plus-thick</v-icon>New Device
            </v-btn>
            <v-btn icon v-on:click="loadItems(); loadStatus()">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
          </div>

          <v-divider />

          <div ref="tableWrapper" class="table-wrapper">
            <v-data-table
              v-bind:headers="headers"
              v-bind:items="filteredItems"
              v-bind:height="tableBodyHeight"
              v-bind:loading="$server.pendingQueries"
              item-key="mac_address"
              hide-default-footer
              v-bind:items-per-page="-1"
              fixed-header
              class="device-table"
            >
              <template v-slot:item="{ item }">
                <tr
                  v-bind:class="{ 'text--disabled': !item.enabled }"
                  v-bind:title="!item.enabled ? 'Disabled' : undefined"
                  class="device-row"
                  v-on:click="editItem(item)"
                >
                  <td>
                    <v-chip
                      v-if="itemsLastSeen[item.wg_public_key]"
                      x-small
                      v-bind:color="isOnline(itemsLastSeen[item.wg_public_key].seconds) ? 'success' : 'error'"
                      text-color="white"
                      class="status-chip"
                    >
                      {{ isOnline(itemsLastSeen[item.wg_public_key].seconds) ? 'Online' : 'Offline' }}
                    </v-chip>
                    <v-chip v-else x-small color="grey" text-color="white" class="status-chip">
                      Unknown
                    </v-chip>
                  </td>
                  <td class="d-flex align-center">
                    <v-icon v-if="!item.enabled" class="mr-1 text--disabled" x-small>mdi-wifi-off</v-icon>
                    <strong>{{ item.name }}</strong>
                  </td>
                  <td><span class="mono caption text--secondary">{{ item.mac_address }}</span></td>
                  <td>
                    <span v-if="item.wg_allowed_ip" class="mono caption primary--text d-inline-flex align-center">
                      {{ item.wg_allowed_ip }}
                      <v-btn icon x-small class="ml-1" v-on:click.stop="clipboard_copy(item.wg_allowed_ip.split('/')[0], 'IP copied')">
                        <v-icon x-small>mdi-content-copy</v-icon>
                      </v-btn>
                      <v-btn icon x-small v-on:click.stop="openSshDialog(item)">
                        <v-icon x-small>mdi-console</v-icon>
                      </v-btn>
                    </span>
                  </td>
                  <td>
                    <v-chip v-if="item.group_name" x-small dark v-bind:color="item.group_color || 'grey'">
                      <v-icon v-if="item.group_is_servers" left x-small>mdi-server</v-icon>
                      {{ item.group_name }}
                    </v-chip>
                  </td>
                  <td><span class="caption">{{ item.customer }}</span></td>
                  <td>
                    <v-tooltip v-if="itemsLastSeen[item.wg_public_key]" top>
                      <template v-slot:activator="{ on }">
                        <span
                          v-on="on"
                          v-bind:class="isOnline(itemsLastSeen[item.wg_public_key].seconds) ? 'success--text' : 'error--text'"
                          class="caption"
                        >{{ lastSeenDesc(itemsLastSeen[item.wg_public_key].seconds) }}</span>
                      </template>
                      <span>{{ dates_dateTimeFull(itemsLastSeen[item.wg_public_key].isoDate) }}</span>
                    </v-tooltip>
                    <span v-else class="caption text--disabled">—</span>
                  </td>
                  <td class="text-right">
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon x-small class="mr-1" v-on="on" v-on:click.stop="wireGuardConfig(item)">
                          <v-icon small>mdi-shield-key-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>WireGuard config</span>
                    </v-tooltip>
                    <v-tooltip top>
                      <template v-slot:activator="{ on }">
                        <v-btn icon x-small class="mr-1" v-on="on" v-on:click.stop="editItem(item)">
                          <v-icon small>mdi-pencil-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit</span>
                    </v-tooltip>
                    <v-menu bottom offset-y>
                      <template v-slot:activator="{ on }">
                        <v-btn icon x-small v-on="on" v-on:click.stop>
                          <v-icon small color="error">mdi-delete-outline</v-icon>
                        </v-btn>
                      </template>
                      <v-btn color="error" depressed small v-on:click.stop="deleteItem(item)">
                        <v-icon left small>mdi-delete</v-icon>Confirm delete
                      </v-btn>
                    </v-menu>
                  </td>
                </tr>
              </template>

              <template v-slot:body.append>
                <tr v-if="displayCount < allFilteredItems.length">
                  <td v-bind:colspan="headers.length" class="text-center pa-2" style="height:40px">
                    <v-progress-circular indeterminate size="18" color="primary" />
                  </td>
                </tr>
              </template>

              <template v-slot:no-data>
                <div class="text-center py-8">
                  <v-icon large class="text--disabled">mdi-monitor-off</v-icon>
                  <div class="text--disabled mt-2">No devices found</div>
                  <v-btn color="primary" outlined small class="mt-3" v-on:click="createItem">
                    <v-icon left small>mdi-plus</v-icon>Add first device
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>

        </v-card>
      </div>

      <!-- Online ratio chart -->
      <div class="chart-col">
        <online-ratio-chart
      v-bind:ratios="groupRatios"
      v-bind:selected-id="filterGroupId"
      v-on:select="filterGroupId = $event"
    />
      </div>

    </div>

    <item-edit
      v-model="currentItem"
      v-bind:groups="groups"
      v-bind:is-admin="session_isAdmin"
      v-bind:last-seen-map="itemsLastSeen"
      v-on:save="saveItem"
      v-on:delete="deleteItem"
      v-on:wg-config="wireGuardConfig"
      v-on:ssh="openSshDialog"
    />
    <item-wire-guard-config v-model="configItemText" v-bind:device-name="configItemName" />

    <!-- SSH dialog -->
    <v-dialog v-model="sshDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon left>mdi-console</v-icon>
          SSH — {{ sshItem && sshItem.name }}
        </v-card-title>
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="mb-8" icon="mdi-information-outline">
            Requires an SSH client registered as system URI handler (e.g. Remmina, PuTTY, Terminal).
          </v-alert>
          <v-form ref="sshForm" lazy-validation>
            <div class="d-flex gap-2">
              <v-text-field
                v-model="sshUsername"
                label="Username"
                outlined dense autofocus
                prepend-inner-icon="mdi-account-outline"
                v-bind:rules="[v => !!v || 'Required']"
                v-on:keydown.enter="launchSsh"
                class="flex-grow-1"
              />
              <v-text-field
                v-model.number="sshPort"
                label="Port"
                outlined dense
                type="number"
                v-bind:rules="[v => (v > 0 && v <= 65535) || 'Invalid']"
                style="max-width:90px"
              />
            </div>
          </v-form>
          <v-text-field
            v-bind:value="`ssh ${sshPort !== 22 ? `-p ${sshPort} ` : ''}${sshUsername || '<username>'}@${sshItem && sshItem.wg_allowed_ip ? sshItem.wg_allowed_ip.split('/')[0] : ''}`"
            label="Command"
            outlined dense readonly
            class="mono-input mt-2"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text v-on:click="sshDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed v-on:click="launchSsh">
            <v-icon left>mdi-console-line</v-icon>Open SSH
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import ItemEdit from "../components/ItemEdit";
import ItemWireGuardConfig from "../components/ItemWireGuardConfig";
import KpiCard from "../components/KpiCard";
import OnlineRatioChart from "../components/OnlineRatioChart";
const ONLINE_THRESHOLD_SECONDS = 900;

export default {
  name: "Main",

  components: { ItemEdit, ItemWireGuardConfig, KpiCard, OnlineRatioChart },

  data: () => ({
    currentItem:      null,
    currentItemIsNew: false,
    displayCount:     25,
    tableBodyHeight:  400,
    headers: [
      { text: "Status",      value: "status",       sortable: false, width: 100 },
      { text: "Name",        value: "name" },
      { text: "MAC Address", value: "mac_address" },
      { text: "WG IP",       value: "wg_allowed_ip" },
      { text: "Group",       value: "group_name" },
      { text: "Customer",    value: "customer" },
      { text: "Last Seen",   value: "last_seen",    sortable: false, width: 110 },
      { text: "",            value: "options",      sortable: false, align: "right", width: 120 }
    ],
    items:          [],
    groups:         [],
    itemsLastSeen:  {},
    tableSearch:    null,
    filterGroupId:  null,
    filterStatus:   null,
    filterEnabled:  null,
    configItemText: null,
    configItemName: null,
    sshDialog:   false,
    sshItem:     null,
    sshUsername: "",
    sshPort:     22,
    intervalId:     0,
    summary:        null
  }),

  computed: {
    onlineCount() {
      return this.items.filter(item => {
        const seen = this.itemsLastSeen[item.wg_public_key];
        return seen && this.isOnline(seen.seconds);
      }).length;
    },

    offlineCount() {
      return this.items.length - this.onlineCount;
    },

    allFilteredItems() {
      let result = this.items;

      if (this.tableSearch) {
        const s = this.tableSearch.toLowerCase();
        result = result.filter(item =>
          (item.name        && item.name.toLowerCase().includes(s)) ||
          (item.mac_address && item.mac_address.toLowerCase().includes(s)) ||
          (item.customer    && item.customer.toLowerCase().includes(s)) ||
          (item.group_name  && item.group_name.toLowerCase().includes(s)) ||
          (item.wg_allowed_ip && item.wg_allowed_ip.includes(s))
        );
      }

      if (this.filterGroupId !== null && this.filterGroupId !== undefined) {
        result = result.filter(item => item.group_id === this.filterGroupId);
      }

      if (this.filterEnabled !== null && this.filterEnabled !== undefined) {
        result = result.filter(item => !!item.enabled === this.filterEnabled);
      }

      if (this.filterStatus === 'online') {
        result = result.filter(item => {
          const seen = this.itemsLastSeen[item.wg_public_key];
          return seen && this.isOnline(seen.seconds);
        });
      } else if (this.filterStatus === 'offline') {
        result = result.filter(item => {
          const seen = this.itemsLastSeen[item.wg_public_key];
          return !seen || !this.isOnline(seen.seconds);
        });
      }

      return result;
    },

    filteredItems() {
      return this.allFilteredItems.slice(0, this.displayCount);
    },

    groupFilterItems() {
      return this.groups.map(g => ({
        value: g.id,
        label: g.is_servers ? `${g.name} (view all devices)` : g.name
      }));
    },

    statusFilterItems() {
      return [
        { value: 'online',  label: 'Online' },
        { value: 'offline', label: 'Offline' }
      ];
    },

    enabledFilterItems() {
      return [
        { value: true,  label: 'Enabled' },
        { value: false, label: 'Disabled' }
      ];
    },

    groupRatios() {
      return this.groups
        .map(g => {
          const groupBoxes = this.items.filter(b => b.group_id === g.id);
          const online = groupBoxes.filter(b => {
            const seen = this.itemsLastSeen[b.wg_public_key];
            return seen && this.isOnline(seen.seconds);
          }).length;
          return { id: g.id, name: g.name, color: g.color, online, total: groupBoxes.length };
        })
        .filter(g => g.total > 0);
    }
  },

  watch: {
    allFilteredItems() {
      this.displayCount = 25;
    }
  },

  mounted() {
    this.loadItems();
    this.loadGroups();
    this.loadStatus();
    this.loadSummary();
    this.intervalId = setInterval(() => {
      if (document.hidden) return; // ponytail: skip polls while tab is in the background
      this.loadStatus();
      this.loadSummary();
    }, 30000);
    this.$nextTick(() => {
      this.computeTableHeight();
      window.addEventListener('resize', this.computeTableHeight);
      this.attachScrollListener();
    });
  },

  beforeDestroy() {
    this.intervalId && clearInterval(this.intervalId);
    window.removeEventListener('resize', this.computeTableHeight);
    this.detachScrollListener();
  },

  methods: {
    computeTableHeight() {
      const el = this.$refs.tableWrapper;
      if (!el) return;
      this.tableBodyHeight = Math.max(200, window.innerHeight - el.getBoundingClientRect().top - 20);
    },

    attachScrollListener() {
      const tableEl = this.$el.querySelector('.v-data-table__wrapper');
      if (tableEl) {
        this._scrollTarget = tableEl;
        tableEl.addEventListener('scroll', this.onTableScroll);
      }
    },

    detachScrollListener() {
      this._scrollTarget && this._scrollTarget.removeEventListener('scroll', this.onTableScroll);
    },

    onTableScroll(e) {
      const el = e.target;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 80) {
        this.displayCount = Math.min(this.displayCount + 25, this.allFilteredItems.length);
      }
    },

    loadItems() {
      this.$server
        .query({ url: "boxes" })
        .then(items => { this.items = items; this.currentItem = null; })
        .catch(() => { this.items = []; });
    },

    loadGroups() {
      this.$server.query({ url: "groups" }).then(groups => (this.groups = groups)).catch(() => {});
    },

    loadStatus() {
      this.$server.query({ url: "boxes/status" }).then(status => {
        const now = Date.now();
        this.itemsLastSeen = Object.keys(status).reduce((map, key) => {
          const entry   = status[key];
          const isoDate = new Date(entry.last_seen);
          map[key] = {
            isoDate,
            seconds:    (now - isoDate.getTime()) / 1000,
            online:     entry.online,
            endpointIp: entry.endpoint_ip,
            rxTotal:    entry.rx_total,
            txTotal:    entry.tx_total
          };
          return map;
        }, {});
      });
    },

    loadSummary() {
      this.$server
        .query({ url: "stats/summary" })
        .then(summary => { this.summary = summary; })
        .catch(() => {});
    },

    isOnline(seconds) {
      return seconds < ONLINE_THRESHOLD_SECONDS;
    },

    createItem() {
      this.currentItemIsNew = true;
      this.currentItem = { enabled: true };
    },

    editItem(item) {
      this.currentItemIsNew = false;
      this.currentItem = Object.assign({}, item);
    },

    saveItem(item) {
      const createKeys = this.session_isAdmin
        ? ["name", "sim", "imei", "enabled", "customer", "notes", "ssh_username", "ssh_port", "mac_address", "wg_allowed_ip", "group_id"]
        : ["name", "sim", "imei", "enabled", "customer", "notes", "ssh_username", "ssh_port", "mac_address", "group_id"];

      const keys = this.currentItemIsNew ? createKeys : ["name", "sim", "enabled", "customer", "notes", "ssh_username", "ssh_port", "group_id"];

      const payload = keys.reduce((acc, key) => {
        key in item && item[key] !== null && item[key] !== "" && (acc[key] = item[key]);
        return acc;
      }, {});

      (this.currentItemIsNew
        ? this.$server.save({ url: "boxes", data: payload })
        : this.$server.update({ url: `boxes/${this.currentItem.mac_address}`, data: payload })
      ).then(() => { this.loadItems(); this.loadGroups(); });
    },

    deleteItem(item) {
      this.$server.delete({ url: `boxes/${item.mac_address}` }).then(this.loadItems);
    },

    wireGuardConfig(item) {
      this.configItemName = item.name || null;
      this.$server
        .query({ url: `boxes/${item.mac_address}/wg-config` })
        .then(conf => (this.configItemText = conf));
    },

    openSshDialog(item) {
      this.currentItem = null;
      this.sshItem     = item;
      this.sshUsername = item.ssh_username || "";
      this.sshPort     = item.ssh_port || 22;
      this.sshDialog   = true;
      this.$nextTick(() => this.$refs.sshForm && this.$refs.sshForm.resetValidation());
    },

    launchSsh() {
      if (!this.$refs.sshForm.validate()) return;
      const host = (this.sshItem.wg_allowed_ip || "").split("/")[0];
      const port = this.sshPort || 22;
      const cmd  = `ssh ${port !== 22 ? `-p ${port} ` : ""}${this.sshUsername}@${host}`;
      this.clipboard_copy(cmd);
      const a    = document.createElement("a");
      a.href = `ssh://${this.sshUsername}@${host}${port !== 22 ? `:${port}` : ""}`;
      a.click();
      this.sshDialog = false;
    },

    lastSeenDesc(seconds) {
      if (seconds < 0) return "Never";
      const minutes = Math.floor(seconds / 60);
      const hours   = Math.floor(seconds / 3600);
      const days    = Math.floor(seconds / 86400);
      if (minutes === 0) return "<1m";
      if (minutes < 60)  return `${minutes}m`;
      if (hours < 24)    return `${hours}h`;
      if (days < 30)     return `${days}d`;
      return ">30d";
    }
  }
};
</script>

<style scoped>
.main-layout {
  height: calc(100vh - 56px);
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto 1fr;
  padding: 20px 20px 0 20px;
  gap: 16px;
  overflow: hidden;
}

.workspace-row {
  position: relative;
  display: grid;
  grid-template-columns: 4.5fr 1fr;
  gap: 16px;
  overflow: hidden;
  padding-bottom: 20px;
}

.table-col     { width: 100%; height: 100%; }
.chart-col     { width: 100%; height: 100%; }
.chart-col > * { height: 100%; }

.table-card    { flex-grow: 1; display: flex; flex-direction: column; min-height: 0; }
.table-toolbar { flex-shrink: 0; }
.table-wrapper { flex-grow: 1; height: 100%; }

.device-row   { cursor: pointer; }
.status-chip  { font-size: 10px !important; }
.gap-2        { gap: 8px; }
.mono-input >>> input { font-family: 'JetBrains Mono', 'Fira Code', monospace !important; font-size: 12px !important; }
</style>
